import React from 'react';
import { Save, History, Download, Share2, ArrowLeft, Eye, EyeOff, Code, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';

interface EditorToolbarProps {
  project: any;
  onSaveVersion: () => void;
  onToggleVersions: () => void;
  onExport?: () => void;
  onShare?: () => void;
  showPreview?: boolean;
  onTogglePreview?: () => void;
  previewMode?: 'development' | 'production';
  onPreviewModeChange?: (mode: 'development' | 'production') => void;
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  project,
  onSaveVersion,
  onToggleVersions,
  onExport,
  onShare,
  showPreview = false,
  onTogglePreview,
  previewMode = 'development',
  onPreviewModeChange,
}) => {
  const { canCommit, canEditFiles } = usePermissions();

  return (
    <div className="h-14 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between px-4">
      <div className="flex items-center gap-4">
        <Link
          to="/dashboard/editor"
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title="Back to Projects"
        >
          <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
        </Link>
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {project?.name || 'Loading...'}
          </h1>
          {project?.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {project.description}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Preview Controls */}
        {onTogglePreview && (
          <>
            <button
              onClick={onTogglePreview}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                showPreview 
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
              }`}
              title={showPreview ? "Hide Preview" : "Show Preview"}
            >
              {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
              <span className="text-sm">Preview</span>
            </button>

            {showPreview && onPreviewModeChange && (
              <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => onPreviewModeChange('development')}
                  className={`px-2 py-1 text-xs ${
                    previewMode === 'development'
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}
                  title="Development mode"
                >
                  <Code size={14} />
                </button>
                <button
                  onClick={() => onPreviewModeChange('production')}
                  className={`px-2 py-1 text-xs ${
                    previewMode === 'production'
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}
                  title="Production mode"
                >
                  <Package size={14} />
                </button>
              </div>
            )}

            <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />
          </>
        )}

        {canCommit && (
          <button
            onClick={onSaveVersion}
            className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            title="Save Version"
          >
            <Save size={16} />
            <span className="text-sm">Save Version</span>
          </button>
        )}

        <button
          onClick={onToggleVersions}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title="Version History"
        >
          <History size={20} className="text-gray-600 dark:text-gray-400" />
        </button>

        {onExport && canEditFiles && (
          <button
            onClick={onExport}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Export"
          >
            <Download size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        )}

        {onShare && canEditFiles && (
          <button
            onClick={onShare}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Share"
          >
            <Share2 size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
};